import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { MOCK_REGULATORY_RECEIPTS } from '../data/mockRegulatoryReceipts';
import type { RegulatoryReceipt } from '../data/regulatoryTypes';

// Single shared source of truth for Regulatory Cell receipts — every screen
// (Material Intake's Regulatory tab, All Receipts' Regulatory tab, Production
// Log, and the capture flow that creates new ones) reads and writes through
// here. Mirrors ProxyDataContext.tsx's diff-overlay-on-an-immutable-seed
// idiom, extended with an `added` list since captures create brand-new
// receipts rather than only patching existing ones.
interface RegulatoryDataContextValue {
  receipts: RegulatoryReceipt[];
  getReceipt: (receiptId: string) => RegulatoryReceipt | undefined;
  addReceipt: (receipt: RegulatoryReceipt) => void;
  updateReceipt: (receiptId: string, patch: Partial<RegulatoryReceipt>) => void;
}

const RegulatoryDataContext = createContext<RegulatoryDataContextValue | null>(null);

const LS_KEY_ADDED = 'opsai.regulatoryReceipts.added.v1';
const LS_KEY_OVERLAY = 'opsai.regulatoryReceipts.overlay.v1';

function safeParse<T>(json: string | null, fallback: T): T {
  try {
    const v = JSON.parse(json ?? '');
    return v ?? fallback;
  } catch {
    return fallback;
  }
}

function writeThrough(key: string, value: unknown) {
  if (typeof window !== 'undefined') window.localStorage.setItem(key, JSON.stringify(value));
}

export function RegulatoryDataProvider({ children }: { children: ReactNode }) {
  const [added, setAdded] = useState<RegulatoryReceipt[]>(() => {
    if (typeof window === 'undefined') return [];
    return safeParse<RegulatoryReceipt[]>(window.localStorage.getItem(LS_KEY_ADDED), []);
  });
  const [overlay, setOverlay] = useState<Record<string, Partial<RegulatoryReceipt>>>(() => {
    if (typeof window === 'undefined') return {};
    return safeParse<Record<string, Partial<RegulatoryReceipt>>>(window.localStorage.getItem(LS_KEY_OVERLAY), {});
  });

  const receipts = useMemo(() => {
    const apply = (r: RegulatoryReceipt) => (overlay[r.receiptId] ? { ...r, ...overlay[r.receiptId] } : r);
    // Newly captured receipts surface first — they're the freshest arrivals.
    return [...added.map(apply), ...MOCK_REGULATORY_RECEIPTS.map(apply)];
  }, [added, overlay]);

  const getReceipt = useCallback(
    (receiptId: string) => receipts.find(r => r.receiptId === receiptId),
    [receipts],
  );

  const addReceipt = useCallback((receipt: RegulatoryReceipt) => {
    setAdded(prev => {
      const next = [receipt, ...prev];
      writeThrough(LS_KEY_ADDED, next);
      return next;
    });
  }, []);

  const updateReceipt = useCallback((receiptId: string, patch: Partial<RegulatoryReceipt>) => {
    setOverlay(prev => {
      const next = { ...prev, [receiptId]: { ...prev[receiptId], ...patch } };
      writeThrough(LS_KEY_OVERLAY, next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ receipts, getReceipt, addReceipt, updateReceipt }),
    [receipts, getReceipt, addReceipt, updateReceipt],
  );

  return <RegulatoryDataContext.Provider value={value}>{children}</RegulatoryDataContext.Provider>;
}

export function useRegulatoryData(): RegulatoryDataContextValue {
  const ctx = useContext(RegulatoryDataContext);
  if (!ctx) throw new Error('useRegulatoryData must be used inside RegulatoryDataProvider');
  return ctx;
}
